import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api', timeout: 10000 });
const POLL_MS = 20000; // 20 seconds

// Crypto symbols trade 24/7 — BTC-USD, ETH-USD, BTC-USDT, etc.
function isCrypto(symbol) {
  return /[-/](USD|USDT|USDC|BTC|ETH|BNB)$/i.test(symbol || '');
}

// Returns { open, tz } for a symbol based on its exchange suffix
function getMarketConfig(symbol) {
  const suffix = (symbol || '').split('.').slice(1).join('.').toUpperCase();

  const markets = [
    // Hong Kong — HKEX: 9:30–12:00, 13:00–16:00 HKT
    { suffixes: ['HK'],        tz: 'Asia/Hong_Kong',    sessions: [[9*60+30, 12*60], [13*60, 16*60]] },
    // Shanghai / Shenzhen — 9:30–11:30, 13:00–15:00 CST
    { suffixes: ['SS', 'SZ'],  tz: 'Asia/Shanghai',     sessions: [[9*60+30, 11*60+30], [13*60, 15*60]] },
    // Singapore — SGX: 9:00–17:00 SGT
    { suffixes: ['SI'],        tz: 'Asia/Singapore',    sessions: [[9*60, 17*60]] },
    // Malaysia — Bursa: 9:00–12:30, 14:30–17:00 MYT
    { suffixes: ['KL'],        tz: 'Asia/Kuala_Lumpur', sessions: [[9*60, 12*60+30], [14*60+30, 17*60]] },
    // Japan — TSE: 9:00–11:30, 12:30–15:30 JST
    { suffixes: ['T', 'TYO'],  tz: 'Asia/Tokyo',        sessions: [[9*60, 11*60+30], [12*60+30, 15*60+30]] },
    // South Korea — KRX: 9:00–15:30 KST
    { suffixes: ['KS', 'KQ'],  tz: 'Asia/Seoul',        sessions: [[9*60, 15*60+30]] },
    // Australia — ASX: 10:00–16:00 AEDT/AEST
    { suffixes: ['AX'],        tz: 'Australia/Sydney',  sessions: [[10*60, 16*60]] },
    // India — NSE/BSE: 9:15–15:30 IST
    { suffixes: ['NS', 'BO'],  tz: 'Asia/Kolkata',      sessions: [[9*60+15, 15*60+30]] },
    // UK — LSE: 8:00–16:30 GMT/BST
    { suffixes: ['L'],         tz: 'Europe/London',     sessions: [[8*60, 16*60+30]] },
    // Germany — XETRA: 9:00–17:30 CET
    { suffixes: ['DE', 'F'],   tz: 'Europe/Berlin',     sessions: [[9*60, 17*60+30]] },
    // Canada — TSX: 9:30–16:00 ET
    { suffixes: ['TO', 'V'],   tz: 'America/Toronto',   sessions: [[9*60+30, 16*60]] },
    // Default: US markets (NYSE/NASDAQ) — 9:30–16:00 ET
    { suffixes: [],            tz: 'America/New_York',  sessions: [[9*60+30, 16*60]] },
  ];

  return markets.find(m => m.suffixes.includes(suffix)) || markets[markets.length - 1];
}

function isMarketOpen(symbol) {
  if (isCrypto(symbol)) return true; // 24/7
  const { tz, sessions } = getMarketConfig(symbol);
  const now = new Date();
  const local = new Date(now.toLocaleString('en-US', { timeZone: tz }));
  const day = local.getDay();
  if (day === 0 || day === 6) return false;
  const mins = local.getHours() * 60 + local.getMinutes();
  return sessions.some(([open, close]) => mins >= open && mins < close);
}

export default function useLivePrice(symbol, initialPrice, initialChange, initialChangePercent, initialQuote) {
  const [price, setPrice] = useState(initialPrice);
  const [change, setChange] = useState(initialChange);
  const [changePercent, setChangePercent] = useState(initialChangePercent);
  const [live, setLive] = useState(false);
  const [extended, setExtended] = useState(null); // { type:'pre'|'post', price, change, changePercent }
  const timerRef = useRef(null);

  // Sync when symbol/initial values change
  useEffect(() => {
    setPrice(initialPrice);
    setChange(initialChange);
    setChangePercent(initialChangePercent);
    setExtended(parseExtended(initialQuote));
  }, [symbol, initialPrice, initialChange, initialChangePercent, initialQuote]);

  useEffect(() => {
    if (!symbol) return;
    const marketOpen = isMarketOpen(symbol);
    if (!marketOpen) { setLive(false); }

    const poll = async () => {
      try {
        const { data } = await api.get(`/stocks/${symbol}/price`);
        if (data?.price) {
          setPrice(data.price);
          setChange(data.change);
          setChangePercent(data.changePercent);
          setLive(isMarketOpen(symbol));
          setExtended(parseExtended(data));
        }
      } catch {}
    };

    poll(); // immediate fetch to get extended hours right away
    timerRef.current = setInterval(poll, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [symbol]);

  return { price, change, changePercent, live, extended };
}

function parseExtended(q) {
  if (!q) return null;
  const state = q.marketState; // PRE, POST, POSTPOST, PREPRE, REGULAR, CLOSED
  if (state === 'PRE' && q.preMarketPrice) {
    return { type: 'pre', price: q.preMarketPrice, change: q.preMarketChange, changePercent: q.preMarketChangePercent };
  }
  if ((state === 'POST' || state === 'POSTPOST') && q.postMarketPrice) {
    return { type: 'post', price: q.postMarketPrice, change: q.postMarketChange, changePercent: q.postMarketChangePercent };
  }
  return null;
}
